import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import FormSelectionDialogContainer from '@/pages/diagnostic/containers/forms/FormSelectionDialogContainer'
import FormSubmissionsList from '@/components/forms/FormSubmissionsList'
import { usePatientFormSubmissions } from '../model/usePatientFormSubmissions'
import { useGenerateFilledPdf } from '@/features/form-submission/model/useGenerateFilledPdf'

type PatientFormsProps = {
  patientId: string
}

export function PatientForms({ patientId }: PatientFormsProps) {
  const { t } = useTranslation()
  const { handleGeneratePdf, generatingPdfId } = useGenerateFilledPdf()

  const [isFormSelectionOpen, setIsFormSelectionOpen] = useState(false)
  const { submissions, totalCount, isLoading, queryError, page, pageSize, setPage, setPageSize } = usePatientFormSubmissions(patientId)

  const handleOpenFormSelection = useCallback(() => {
    setIsFormSelectionOpen(true)
  }, [])

  const handleCloseFormSelection = useCallback(() => {
    setIsFormSelectionOpen(false)
  }, [])

  const handleFormTemplateSelect = useCallback(
    (templateId: string) => {
      const url = `/forms/submit/${templateId}/${patientId}?type=patient`
      window.open(url, '_blank')
      handleCloseFormSelection()
    },
    [patientId]
  )

  const handleEditDraft = useCallback(
    (submissionId: string) => {
      const templateId = submissions.find((s) => s._id === submissionId)?.template._id
      if (!templateId) return
      const url = `/forms/submit/${templateId}/${patientId}/${submissionId}?type=patient`
      window.open(url, '_blank')
    },
    [patientId, submissions]
  )

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage)
  }, [])

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize)
    setPage(0)
  }, [])

  const error = useMemo(() => {
    return queryError ? new Error("An error occurred while loading submissions") : undefined
  }, [queryError])

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">{t('page.infoPatient.forms.title')}</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenFormSelection}>
          {t('page.infoPatient.forms.newForm')}
        </Button>
      </Stack>

      <Box>
        <FormSubmissionsList
          submissions={submissions}
          totalCount={totalCount}
          page={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          isLoading={isLoading}
          error={error}
          onEditDraft={handleEditDraft}
          onGeneratePdf={handleGeneratePdf}
          generatingPdfId={generatingPdfId}
        />
      </Box>

      <FormSelectionDialogContainer open={isFormSelectionOpen} onClose={handleCloseFormSelection} onSelect={handleFormTemplateSelect} />
    </Stack>
  )
}
